import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  Dropdown,
  ButtonGroup,
  Pagination,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

/**
 * Admin view of communities with search, sorting, simple pagination,
 * realtime updates, and delete with confirmation.
 *
 * Expects community docs to have the schema:
 * {
 *   id, name, nameSlug, address, description,
 *   members: [uid,...], association: [], events: [],
 *   createdAt: serverTimestamp()
 * }
 */
export default function ViewCommunities() {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("createdAtDesc"); // createdAtDesc | nameAsc | nameDesc
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const navigate = useNavigate();

  // Realtime fetch communities
  useEffect(() => {
    setLoadError("");
    try {
      const q = query(
        collection(db, "communities"),
        orderBy("createdAt", "desc")
      );

      const unsub = onSnapshot(
        q,
        (snapshot) => {
          const rows = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setCommunities(rows);
          setLoading(false);
        },
        (error) => {
          console.error("onSnapshot error:", error.code, error.message);
          setLoadError(error?.message || "Failed to load communities.");
          setLoading(false);
        }
      );

      return () => unsub();
    } catch (err) {
      console.error("Query error:", err);
      setLoadError("Failed to start listener.");
      setLoading(false);
    }
  }, []);

  // Filter by search
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return communities;

    return communities.filter((c) => {
      const idMatch = String(c.id || "").toLowerCase().includes(term);
      const nameMatch = String(c.name || "").toLowerCase().includes(term);
      const addressMatch = String(c.address || "").toLowerCase().includes(term);
      return idMatch || nameMatch || addressMatch;
    });
  }, [communities, searchTerm]);

  // Sort
  const sorted = useMemo(() => {
    const copy = [...filtered];
    switch (sortKey) {
      case "nameAsc":
        return copy.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      case "nameDesc":
        return copy.sort((a, b) => String(b.name || "").localeCompare(String(a.name || "")));
      case "createdAtDesc":
      default:
        return copy.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });
    }
  }, [filtered, sortKey]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    // Reset to page 1 when search or sort changes
    setCurrentPage(1);
  }, [searchTerm, sortKey]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage]);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "This will permanently delete the community document.\n\n" +
        "Note: You may also need to remove this community ID from associated users (users/{uid}.communities).\n\n" +
        "Continue?"
    );
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "communities", id));
      // State will auto-update via onSnapshot
      alert("Community deleted successfully.");
    } catch (err) {
      console.error("Delete error:", err.code, err.message);
      alert(err?.message || "Failed to delete community.");
    }
  };

  const handleView = (id) => {
    // Navigate to a community detail page
    navigate(`/communities/${id}`);
  };

  return (
    <div>
      <h3>Communities</h3>

      {/* Errors */}
      {loadError && (
        <Alert variant="danger" className="mb-3">
          {loadError}
        </Alert>
      )}

      {/* Search & Sort Toolbar */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Form.Control
          type="text"
          placeholder="Search by ID, Name, or Address"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 320 }}
        />

        <Dropdown as={ButtonGroup}>
          <Button variant="outline-secondary" disabled>
            Sort
          </Button>
          <Dropdown.Toggle split variant="secondary" id="sort-dropdown" />
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setSortKey("createdAtDesc")}>
              Newest first
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setSortKey("nameAsc")}>
              Name (A → Z)
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setSortKey("nameDesc")}>
              Name (Z → A)
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Loading */}
      {loading && (
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading communities…</span>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th style={{ width: 160 }}>ID</th>
                <th>Name</th>
                <th>Address</th>
                <th style={{ width: 160 }}>Created</th>
                <th style={{ width: 140 }}>Members</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No communities found
                  </td>
                </tr>
              ) : (
                pageItems.map((c) => {
                  const created =
                    c.createdAt?.toDate?.()?.toLocaleString?.() || "—";
                  const membersCount = Array.isArray(c.members)
                    ? c.members.length
                    : 0;

                  return (
                    <tr key={c.id}>
                      <td><code>{c.id}</code></td>
                      <td>{c.name || "—"}</td>
                      <td>{c.address || "—"}</td>
                      <td>{created}</td>
                      <td>{membersCount}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleView(c.id)}
                        >
                          View
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(c.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Page {currentPage} of {totalPages} • {sorted.length} total
              </div>
              <Pagination className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} />
                <Pagination.Item active>{currentPage}</Pagination.Item>
                <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
}
